import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Circle, ChevronDown, ChevronUp, Download, Award, ChevronLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const STATUS_STYLES = {
  not_started: { label: 'Not Started', cls: 'bg-earth-100 text-earth-500' },
  in_progress: { label: 'In Progress', cls: 'bg-saffron-100 text-saffron-700' },
  completed: { label: 'Completed', cls: 'bg-green-100 text-green-700' },
};

export default function InternshipWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [enrollment, setEnrollment] = useState(null);
  const [openTask, setOpenTask] = useState(null);
  const [quizState, setQuizState] = useState({}); // taskId → {open, questions, answers, result}
  const [loading, setLoading] = useState(true);
  const [downloadingCert, setDownloadingCert] = useState(false);
  const [downloadingDoc, setDownloadingDoc] = useState(false);

  const load = useCallback(async () => {
    try {
      const [projRes, taskRes] = await Promise.all([
        api.get(`/internships/${id}`),
        api.get(`/internships/${id}/tasks`),
      ]);
      setProject(projRes.data.project);
      setTasks(taskRes.data.tasks);
      setEnrollment(taskRes.data.enrollment);
    } catch (err) {
      if (err.response?.status === 403) navigate(`/internships/${id}`);
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    load();
  }, [isAuthenticated, load, navigate]);

  const startTask = async (taskId) => {
    await api.post(`/internships/${id}/tasks/${taskId}/start`);
    setTasks(prev => prev.map(t => t.id === taskId && t.status === 'not_started' ? { ...t, status: 'in_progress' } : t));
  };

  const openQuiz = async (task) => {
    if (quizState[task.id]?.open) {
      setQuizState(prev => ({ ...prev, [task.id]: { ...prev[task.id], open: false } }));
      return;
    }
    try {
      const res = await api.get(`/internships/${id}/tasks/${task.id}/quiz`);
      setQuizState(prev => ({
        ...prev,
        [task.id]: {
          open: true,
          questions: res.data.questions,
          attempts_used: res.data.attempts_used,
          answers: {},
          result: null,
        }
      }));
    } catch {}
  };

  const submitQuiz = async (taskId) => {
    const qs = quizState[taskId];
    try {
      const res = await api.post(`/internships/${id}/tasks/${taskId}/quiz`, { answers: qs.answers });
      setQuizState(prev => ({ ...prev, [taskId]: { ...prev[taskId], result: res.data } }));
      if (res.data.passed) {
        const taskRes = await api.get(`/internships/${id}/tasks`);
        setTasks(taskRes.data.tasks);
        setEnrollment(taskRes.data.enrollment);
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Submission failed');
    }
  };

  const downloadCertificate = async () => {
    setDownloadingCert(true);
    try {
      const res = await api.get(`/internships/${id}/certificate`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certificate_${id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { alert('Could not download certificate'); }
    setDownloadingCert(false);
  };

  const downloadDocument = async () => {
    setDownloadingDoc(true);
    try {
      const res = await api.get(`/internships/${id}/document`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `project_${id}_guide.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { alert('Could not download project guide'); }
    setDownloadingDoc(false);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-10 h-10 border-4 border-saffron-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const completedCount = tasks.filter(t => t.status === 'completed').length;
  const isCompleted = enrollment?.completion_date;

  return (
    <div className="pt-24 pb-16 bg-background min-h-screen">
      {/* Header */}
      <div className="bg-earth-900 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <Link to="/internships" className="inline-flex items-center text-earth-300 hover:text-saffron-400 text-sm mb-4 gap-1 transition-colors">
            <ChevronLeft className="w-4 h-4" /> Back to Internships
          </Link>
          <h1 className="text-2xl md:text-3xl font-serif font-bold text-earth-50 mb-3">{project?.title}</h1>
          <div className="flex items-center gap-4">
            <div className="flex-1 bg-earth-700 rounded-full h-2.5 overflow-hidden max-w-xs">
              <div
                className="h-full bg-saffron-400 rounded-full transition-all"
                style={{ width: `${(completedCount / (tasks.length || 1)) * 100}%` }}
              />
            </div>
            <span className="text-saffron-400 font-semibold text-sm">{completedCount}/{tasks.length} tasks</span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-4">
        {/* Completion banner */}
        {isCompleted && (
          <motion.div
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            className="bg-green-50 border border-green-200 rounded-2xl p-5 flex flex-col sm:flex-row items-center gap-4"
          >
            <Award className="w-10 h-10 text-green-600 flex-shrink-0" />
            <div className="flex-1 text-center sm:text-left">
              <p className="font-bold text-green-800 text-lg font-serif">Congratulations! Internship Completed 🎉</p>
              <p className="text-green-600 text-sm">Download your certificate and project guide below.</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={downloadCertificate}
                disabled={downloadingCert}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-2 rounded-xl text-sm transition-colors disabled:bg-green-300"
              >
                <Award className="w-4 h-4" /> {downloadingCert ? 'Generating…' : 'Certificate'}
              </button>
              <button
                onClick={downloadDocument}
                disabled={downloadingDoc}
                className="flex items-center gap-2 bg-white border border-green-300 hover:border-green-500 text-green-700 font-semibold px-4 py-2 rounded-xl text-sm transition-colors"
              >
                <Download className="w-4 h-4" /> {downloadingDoc ? 'Loading…' : 'Project Guide'}
              </button>
            </div>
          </motion.div>
        )}

        {/* Download doc always if enrolled */}
        {!isCompleted && (
          <div className="flex justify-end">
            <button
              onClick={downloadDocument}
              disabled={downloadingDoc}
              className="flex items-center gap-2 bg-card border border-earth-200 hover:border-saffron-400 text-earth-600 hover:text-saffron-700 font-medium px-4 py-2 rounded-xl text-sm transition-colors"
            >
              <Download className="w-4 h-4" /> {downloadingDoc ? 'Loading…' : 'Download Project Guide'}
            </button>
          </div>
        )}

        {/* Task accordions */}
        {tasks.map((task, i) => {
          const isOpen = openTask === task.id;
          const qs = quizState[task.id];
          const prevCompleted = i === 0 || tasks[i - 1]?.status === 'completed';

          return (
            <div key={task.id} className={`bg-card rounded-2xl border transition-all ${isOpen ? 'border-saffron-300' : 'border-earth-200'}`}>
              {/* Task header */}
              <button
                className="w-full flex items-center gap-4 p-5 text-left"
                onClick={() => {
                  setOpenTask(isOpen ? null : task.id);
                  if (task.status === 'not_started' && prevCompleted) startTask(task.id);
                }}
              >
                <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${task.status === 'completed' ? 'bg-green-500 text-white' : 'bg-earth-100 text-earth-600'}`}>
                  {task.status === 'completed' ? <CheckCircle className="w-5 h-5" /> : task.task_number}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-earth-900 truncate">{task.title}</p>
                  <p className="text-xs text-earth-400 mt-0.5">~{task.expected_hours} hours</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full font-medium flex-shrink-0 ${STATUS_STYLES[task.status].cls}`}>
                  {STATUS_STYLES[task.status].label}
                </span>
                {isOpen ? <ChevronUp className="w-5 h-5 text-earth-400 flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-earth-400 flex-shrink-0" />}
              </button>

              <AnimatePresence>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="px-5 pb-5 border-t border-earth-100 pt-4 space-y-4">
                      <div>
                        <h4 className="font-semibold text-earth-700 mb-1">What you'll build</h4>
                        <p className="text-earth-500 text-sm">{task.description}</p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-earth-700 mb-2">Step-by-step instructions</h4>
                        <div className="bg-earth-50 border border-earth-100 rounded-xl p-4 text-sm text-earth-600 whitespace-pre-wrap leading-relaxed">
                          {task.instructions}
                        </div>
                      </div>

                      {/* Quiz section */}
                      {task.status !== 'completed' && (
                        <div>
                          <button
                            onClick={() => openQuiz(task)}
                            className="bg-saffron-600 hover:bg-saffron-700 text-white font-semibold px-5 py-2 rounded-xl text-sm transition-colors"
                          >
                            {qs?.open ? 'Hide Quiz' : 'Take Quiz to Complete Task'}
                          </button>
                          {task.quiz_attempts >= 3 && task.status !== 'completed' && (
                            <p className="text-red-500 text-xs mt-2">Maximum attempts reached. Review the task guide and try again.</p>
                          )}
                        </div>
                      )}

                      {/* Quiz inline */}
                      {qs?.open && !qs.result && (
                        <div className="border border-saffron-200 rounded-xl p-4 bg-saffron-50">
                          <p className="font-semibold text-earth-800 mb-1 font-serif">Quiz — Task {task.task_number}</p>
                          <p className="text-xs text-earth-500 mb-4">Pass 3 of 5 questions. Attempt {qs.attempts_used + 1} of 3.</p>
                          <div className="space-y-4">
                            {qs.questions.map((q, qi) => (
                              <div key={q.id}>
                                <p className="font-medium text-earth-700 text-sm mb-2">{qi + 1}. {q.question}</p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                  {['a', 'b', 'c', 'd'].map(opt => (
                                    <label
                                      key={opt}
                                      className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer border text-sm transition-colors ${
                                        qs.answers[q.id] === opt
                                          ? 'border-saffron-400 bg-saffron-100'
                                          : 'border-earth-200 bg-white hover:border-saffron-300'
                                      }`}
                                    >
                                      <input
                                        type="radio"
                                        name={`q_${q.id}`}
                                        value={opt}
                                        checked={qs.answers[q.id] === opt}
                                        onChange={() => setQuizState(prev => ({
                                          ...prev,
                                          [task.id]: { ...prev[task.id], answers: { ...prev[task.id].answers, [q.id]: opt } }
                                        }))}
                                        className="accent-saffron-600"
                                      />
                                      <span className="uppercase font-semibold text-earth-500 mr-1">{opt}.</span>
                                      {q[`option_${opt}`]}
                                    </label>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                          <button
                            onClick={() => submitQuiz(task.id)}
                            disabled={Object.keys(qs.answers).length < qs.questions.length}
                            className="mt-4 bg-earth-900 hover:bg-earth-800 disabled:bg-earth-200 text-white font-semibold px-6 py-2 rounded-xl text-sm transition-colors"
                          >
                            Submit Quiz
                          </button>
                        </div>
                      )}

                      {/* Quiz result */}
                      {qs?.result && (
                        <div className={`rounded-xl p-4 border ${qs.result.passed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                          <p className={`font-bold text-lg ${qs.result.passed ? 'text-green-700' : 'text-red-600'}`}>
                            {qs.result.passed ? '✅ Passed!' : '❌ Not quite…'} — Score: {qs.result.score}/{qs.result.total}
                          </p>
                          {qs.result.passed ? (
                            <p className="text-green-600 text-sm mt-1">Task marked as completed. Move to the next one!</p>
                          ) : (
                            <p className="text-red-500 text-sm mt-1">
                              {qs.result.attempts_used < 3
                                ? `Review the instructions and try again. ${3 - qs.result.attempts_used} attempt(s) remaining.`
                                : 'No more attempts. Please review the task and come back.'}
                            </p>
                          )}
                          {!qs.result.passed && qs.result.attempts_used < 3 && (
                            <button
                              onClick={() => setQuizState(prev => ({
                                ...prev,
                                [task.id]: { ...prev[task.id], result: null, answers: {}, attempts_used: qs.result.attempts_used }
                              }))}
                              className="mt-2 text-sm text-red-600 underline"
                            >
                              Try again
                            </button>
                          )}
                        </div>
                      )}

                      {task.status === 'completed' && (
                        <div className="flex items-center gap-2 text-green-600 text-sm">
                          <CheckCircle className="w-4 h-4" />
                          Task completed · Score: {task.quiz_score}/5 · {new Date(task.completed_at).toLocaleDateString('en-IN')}
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}

        {/* Certificate download if completed (bottom) */}
        {isCompleted && (
          <div className="text-center pt-4">
            <button
              onClick={downloadCertificate}
              disabled={downloadingCert}
              className="inline-flex items-center gap-2 bg-saffron-600 hover:bg-saffron-700 text-white font-bold px-8 py-3 rounded-xl text-base transition-colors"
            >
              <Award className="w-5 h-5" /> {downloadingCert ? 'Generating Certificate…' : 'Download Certificate'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
